/**
 * Serviço de Cache para URLs Assinadas do Oracle Storage
 * 
 * Gerencia cache local de URLs assinadas para evitar requisições repetidas.
 * URLs são válidas por 24h, mas renovamos após 23h para garantir disponibilidade.
 */

interface CachedUrl {
  url: string;
  tipo: 'oracle' | 'local' | 'local-fallback';
  expiraEm: string; // ISO 8601
  cachedAt: number; // timestamp
}

const CACHE_KEY_PREFIX = 'oracle_url_cache_';
const CACHE_DURATION_MS = 23 * 60 * 60 * 1000; // 23 horas para URLs Oracle válidas
const FALLBACK_CACHE_DURATION_MS = 2 * 60 * 1000; // ⚠️ APENAS 2 MINUTOS para fallbacks (para tentar novamente logo)

class OracleUrlCacheService {
  /**
   * Obter URL do cache se ainda válida
   */
  get(resourceType: string, resourceId: string | number): string | null {
    const cacheKey = this.getCacheKey(resourceType, resourceId);
    
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) {
        return null;
      }
      
      const data: CachedUrl = JSON.parse(cached);
      
      // ✅ CORREÇÃO: Usar cache mais curto para fallbacks (2min) para tentar novamente logo
      const maxCacheAge = data.tipo === 'local-fallback' ? FALLBACK_CACHE_DURATION_MS : CACHE_DURATION_MS;
      
      // Verificar se ainda está válido
      const agora = Date.now();
      const cacheAge = agora - data.cachedAt;
      
      if (cacheAge < maxCacheAge) {
        const ageMin = Math.round(cacheAge / 1000 / 60);
        const maxAgeMin = Math.round(maxCacheAge / 1000 / 60);
        // console.log(`[ORACLE-CACHE] ✅ Hit para ${resourceType}/${resourceId} (idade: ${ageMin}min/${maxAgeMin}min, tipo: ${data.tipo})`);
        return data.url;
      }
      
      // Cache expirado
      // console.log(`[ORACLE-CACHE] ⏰ Cache expirado para ${resourceType}/${resourceId} (tipo: ${data.tipo})`);
      this.remove(resourceType, resourceId);
      return null;
      
    } catch (error) {
      console.error('[ORACLE-CACHE] Erro ao ler cache:', error);
      return null;
    }
  }
  
  /**
   * Salvar URL no cache
   */
  set(
    resourceType: string,
    resourceId: string | number,
    url: string,
    tipo: 'oracle' | 'local' | 'local-fallback',
    expiraEm?: string
  ): void {
    const cacheKey = this.getCacheKey(resourceType, resourceId);
    
    const data: CachedUrl = {
      url,
      tipo,
      expiraEm: expiraEm || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      cachedAt: Date.now()
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      // console.log(`[ORACLE-CACHE] 💾 Salvou ${resourceType}/${resourceId} (tipo: ${tipo})`);
    } catch (error) {
      console.error('[ORACLE-CACHE] Erro ao salvar cache:', error);
      
      // Se localStorage estiver cheio, limpar caches antigos
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {

        this.cleanupOldCaches();
        
        // Tentar novamente
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (retryError) {
          console.error('[ORACLE-CACHE] Falha ao salvar após limpeza:', retryError);
        }
      }
    }
  }
  
  /**
   * Remover URL do cache
   */
  remove(resourceType: string, resourceId: string | number): void {
    const cacheKey = this.getCacheKey(resourceType, resourceId);
    localStorage.removeItem(cacheKey);

  }
  
  /**
   * Limpar todos os caches de um tipo de recurso
   */
  clearResourceType(resourceType: string): void {
    const prefix = `${CACHE_KEY_PREFIX}${resourceType}_`;
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));

  }
  
  /**
   * Limpar TODOS os caches Oracle
   */
  clearAll(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    // console.log(`[ORACLE-CACHE] 🗑️ Limpou TODOS os caches (${keysToRemove.length} itens)`);
  }
  
  /**
   * Limpar caches antigos (mais de 24h)
   */
  cleanupOldCaches(): void {
    const agora = Date.now();
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CACHE_KEY_PREFIX)) continue;
      
      try {
        const cached = localStorage.getItem(key);
        if (!cached) continue;
        
        const data: CachedUrl = JSON.parse(cached);
        const cacheAge = agora - data.cachedAt;
        
        // Remover se tiver mais de 24h
        if (cacheAge > 24 * 60 * 60 * 1000) {
          keysToRemove.push(key);
        }
      } catch (error) {
        // Se houver erro ao parsear, remover
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (keysToRemove.length > 0) {

    }
  }
  
  /**
   * Limpar TODOS os fallbacks do cache (útil após corrigir backend)
   */
  clearAllFallbacks(): number {
    let removed = 0;
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CACHE_KEY_PREFIX)) continue;
      
      try {
        const cached = localStorage.getItem(key);
        if (!cached) continue;
        
        const data: CachedUrl = JSON.parse(cached);
        
        // Remover fallbacks e locals (manter apenas oracle)
        if (data.tipo === 'local-fallback' || data.tipo === 'local') {
          keysToRemove.push(key);
        }
      } catch (error) {
        // Ignorar erros
      }
    }
    
    // Remover fora do loop para evitar problemas de índice
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      removed++;
    });

    return removed;
  }
  
  /**
   * Obter estatísticas do cache
   */
  getStats(): {
    total: number;
    porTipo: Record<string, number>;
    maisAntigo: number | null;
    maisRecente: number | null;
  } {
    const stats = {
      total: 0,
      porTipo: {} as Record<string, number>,
      maisAntigo: null as number | null,
      maisRecente: null as number | null
    };
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CACHE_KEY_PREFIX)) continue;
      
      try {
        const cached = localStorage.getItem(key);
        if (!cached) continue;
        
        const data: CachedUrl = JSON.parse(cached);
        stats.total++;
        
        // Contar por tipo
        stats.porTipo[data.tipo] = (stats.porTipo[data.tipo] || 0) + 1;
        
        // Rastrear mais antigo/recente
        if (stats.maisAntigo === null || data.cachedAt < stats.maisAntigo) {
          stats.maisAntigo = data.cachedAt;
        }
        if (stats.maisRecente === null || data.cachedAt > stats.maisRecente) {
          stats.maisRecente = data.cachedAt;
        }
      } catch (error) {
        // Ignorar erros de parse
      }
    }
    
    return stats;
  }
  
  /**
   * Gerar chave de cache
   */
  private getCacheKey(resourceType: string, resourceId: string | number): string {
    return `${CACHE_KEY_PREFIX}${resourceType}_${resourceId}`;
  }
}

// Exportar instância singleton
export const oracleUrlCache = new OracleUrlCacheService();

// Limpar caches antigos ao carregar
oracleUrlCache.cleanupOldCaches();
